import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

/**
 * Back in stock — captures an email against a sold-out variant so the shopper
 * hears when it returns, instead of leaving with nothing.
 *
 * Posts straight to Klaviyo's public client endpoint. That endpoint is designed
 * to be called from the browser with the site's public company ID (the same one
 * already in the onsite script on every page), so there is no key here that is
 * not public, and no server of our own in the path.
 *
 * The block re-renders with the rest of the section on every variant change, so
 * this component never has to track which variant is selected — it reads the
 * one the server just rendered it for.
 */

const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
const KLAVIYO_REVISION = '2024-10-15';

/**
 * @typedef {object} BackInStockRefs
 * @property {HTMLFormElement} [form]
 * @property {HTMLInputElement} [email]
 * @property {HTMLButtonElement} [submit]
 * @property {HTMLElement} [message]
 *
 * @extends Component<BackInStockRefs>
 */
class BackInStock extends Component {
  #submitting = false;

  connectedCallback() {
    super.connectedCallback();
    this.#section()?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section()?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  #section() {
    return this.closest('.shopify-section, dialog');
  }

  /**
   * Picking a variant morphs only the variant picker, so this component has to
   * update itself from the HTML the event carries — the same contract
   * product-inventory uses. Without this the form can never appear on a
   * sold-out variant chosen after page load.
   *
   * @param {Event & { promise?: Promise<{ detail?: { html?: Document, productId?: string, newProduct?: { id: string } } }> }} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    if (!event.promise) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;
        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && this.dataset.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const next = html.querySelector('back-in-stock-component');
        if (!next) return;

        // The variant id lives on the wrapper, and a children-only morph would
        // leave it pointing at whichever variant first rendered — subscribing
        // the shopper to the wrong size. Sync it explicitly.
        for (const { name, value } of Array.from(next.attributes)) {
          if (name.startsWith('data-') && this.getAttribute(name) !== value) this.setAttribute(name, value);
        }
        this.toggleAttribute('hidden', next.hasAttribute('hidden'));

        morph(this, next, { childrenOnly: true });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[back-in-stock] Event promise rejected:', error);
      });
  };

  /** @param {Event} event */
  async handleSubmit(event) {
    event.preventDefault();
    if (this.#submitting) return;

    const { email, message, submit } = this.refs;
    if (!(email instanceof HTMLInputElement)) return;

    const address = email.value.trim();
    // Let the browser say what is wrong with the address rather than
    // duplicating its validation, which is better than ours and localised.
    if (!address || !email.checkValidity()) {
      email.reportValidity();
      return;
    }

    const companyId = this.dataset.companyId;
    const variantId = this.dataset.variantId;

    if (!companyId || !variantId) {
      this.#paint('error', this.dataset.errorText ?? '');
      return;
    }

    this.#submitting = true;
    if (submit instanceof HTMLButtonElement) submit.disabled = true;
    this.#paint('pending', this.dataset.pendingText ?? '');

    try {
      const response = await fetch(`${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(companyId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: { data: { type: 'profile', attributes: { email: address } } },
            },
            relationships: {
              variant: {
                // Klaviyo's Shopify catalogue keys variants as
                // $shopify:::$default:::<variant id>.
                data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` },
              },
            },
          },
        }),
      });

      // A 202 is the documented success; anything else means the subscription
      // was not recorded, and saying "we'll let you know" would be a lie.
      if (!response.ok) throw new Error(`Klaviyo responded ${response.status}`);

      this.#paint('success', this.dataset.successText ?? '');
      if (this.refs.form instanceof HTMLFormElement) this.refs.form.hidden = true;
    } catch (error) {
      console.warn('[back-in-stock] subscription failed:', error);
      this.#paint('error', this.dataset.errorText ?? '');
      if (submit instanceof HTMLButtonElement) submit.disabled = false;
    } finally {
      this.#submitting = false;
    }
  }

  /**
   * @param {'pending' | 'success' | 'error'} state
   * @param {string} text
   */
  #paint(state, text) {
    const { message } = this.refs;
    if (!(message instanceof HTMLElement)) return;

    message.textContent = text;
    message.dataset.state = state;
    message.hidden = !text;
  }
}

if (!customElements.get('back-in-stock-component')) {
  customElements.define('back-in-stock-component', BackInStock);
}
